import { useQueryClient } from '@tanstack/react-query';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Spinner } from '@ekhodealer/ekho-common/rev';

import type { ServicesProvider } from '@ekhodealer/ekho-common';

import { servicesProviders } from '@common/api/services-providers/queryKeys';
import { getServicesProvider } from '@common/api/services-providers/useGetServicesProvider';
import { paths } from '@config/paths';
import { SettingsSection } from '@config/paths/settingsSections';
import { apiClient } from '@config/server/apiClient';
import { productOnboardingEndpoints } from '@config/server/apiEndpoints';
import { POST_SETUP_REDIRECT_KEY } from '@config/storageKeys';

import { useGetOnboardingRecord } from '../api/useGetOnboardingRecord';
import { useProvisionOnboarding } from '../api/useProvisionOnboarding';
import {
	hasPreSelectedProductAtom,
	onboardingRecordIdAtom,
	onboardingStepAtom,
} from '../atoms/onboardingAtoms';
import { OnboardingStatus } from '../types';

import './OnboardingSteps.scss';

const LOG_PREFIX = '[OnboardingProcessing]';

/**
 * Step 4 — Processing Payment & Provisioning
 *
 * Reached after the user successfully submits payment in step 3
 * (OnboardingSubscribeStep). This step polls for subscription activation
 * and then provisions the dealer account.
 *
 * Supports two modes (same as OnboardingSubscribeStep):
 *
 * **Path 1 — Net-new dealer (public, no auth):**
 *   - Polls OnboardingRecord for SUBSCRIBED status
 *   - On confirmation, calls provision endpoint to create SVP + Auth + Invitation
 *   - Redirects to the invitation URL
 *
 * **Path 2 — Existing dealer (authenticated):**
 *   - Polls for BDC activation (webhook sets config.bdc.supported)
 *   - On confirmation, calls complete-onboarding endpoint
 *   - Redirects to AI BDC product page
 */
export function OnboardingProcessingStep() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const setStep = useSetAtom(onboardingStepAtom);
	const [recordId] = useAtom(onboardingRecordIdAtom);
	const hasPreSelected = useAtomValue(hasPreSelectedProductAtom);
	const { svpId } = useParams();
	const servicesProviderId = svpId ?? null;
	const isExistingDealer = !!svpId;

	// Path 1: poll the OnboardingRecord for status changes
	const { refetch: refetchRecord } = useGetOnboardingRecord(recordId);

	const [isProvisioning, setIsProvisioning] = useState(false);
	const [pollingTimedOut, setPollingTimedOut] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const hasStartedPolling = useRef(false);

	const provisionMutation = useProvisionOnboarding();

	// Set step index on mount:
	//   Pre-selected existing dealer → step 1 (Processing is second in PRE_SELECTED_STEPS)
	//   Existing dealer (no pre-select) → step 2 (Processing is third in EXISTING_DEALER_STEPS)
	//   New dealer → step 3 (Processing is fourth in NEW_DEALER_STEPS)
	useEffect(() => {
		if (isExistingDealer && hasPreSelected) {
			setStep(1);
		} else if (isExistingDealer) {
			setStep(2);
		} else {
			setStep(3);
		}
	}, [setStep, isExistingDealer, hasPreSelected]);

	// Gate: redirect if we have neither a record ID nor an existing dealer context
	useEffect(() => {
		if (!recordId && !isExistingDealer) {
			navigate(paths.onboarding.getHref(), { replace: true });
		}
	}, [recordId, isExistingDealer, navigate]);

	// Path 1 post-payment: provision SVP + Auth + Invitation and redirect to invite URL
	const provisionAndRedirect = useCallback(async (): Promise<void> => {
		if (!recordId || isProvisioning) return;

		setIsProvisioning(true);

		try {
			const result = await provisionMutation.mutateAsync(recordId);

			if (result.inviteUrl) {
				// Store in localStorage before redirecting — survives email verification
				// link opening in a new tab (sessionStorage is tab-scoped and would be lost)
				localStorage.setItem(POST_SETUP_REDIRECT_KEY, 'ai-bdc');
				const separator = result.inviteUrl.includes('?') ? '&' : '?';
				window.location.href = `${result.inviteUrl}${separator}from=onboarding`;
			} else {
				navigate(paths.home.getHref(), { replace: true });
			}
		} catch (err) {
			console.error(LOG_PREFIX, 'Provisioning failed:', err);
			setIsProvisioning(false);
			setError('Payment confirmed but account setup failed. Please contact support.');
		}
	}, [recordId, isProvisioning, provisionMutation, navigate]);

	// Path 2 post-payment: call complete-onboarding to guarantee supported=true server-side,
	// then poll until the flag is readable from Firestore before seeding both TanStack Query
	// caches and navigating. complete-onboarding sets supported=true itself, so we don't
	// need to wait for the Stripe webhook before calling this.
	const completeExistingAndRedirect = useCallback(async (): Promise<void> => {
		if (!servicesProviderId) return;
		try {
			await apiClient.post(
				productOnboardingEndpoints.completeOnboarding(servicesProviderId),
				{},
			);
		} catch (err) {
			// Log but don't block navigation — the user paid, proceed regardless
			console.warn(LOG_PREFIX, 'complete-onboarding call failed, proceeding to navigate:', err);
		}

		// Poll until bdc.supported === true is readable from Firestore (set by complete-onboarding above).
		let verifiedSvp: ServicesProvider | null = null;
		for (let i = 0; i < 10 && !verifiedSvp; i++) {
			if (i > 0) await new Promise<void>((r) => setTimeout(r, 1000));
			try {
				const svp = await getServicesProvider(servicesProviderId);
				if (svp?.config?.bdc?.supported === true) verifiedSvp = svp;
			} catch {
				// ignore transient fetch errors; keep polling
			}
		}

		if (!verifiedSvp) {
			console.warn(LOG_PREFIX, 'bdc.supported still false after retries; navigating anyway');
			navigate(paths.settingsSection.getHref(servicesProviderId, SettingsSection.ProductsAiBdc), {
				replace: true,
			});
			return;
		}

		// Write verified SVP into both the detail cache and every batch cache entry.
		// The batch cache (populated on app load) is used by useGetUserServicesProviders
		// to seed individual detail caches on portal mount — if we don't update it here
		// it will overwrite our fresh data immediately after navigation.
		queryClient.setQueryData(servicesProviders.detail(servicesProviderId).queryKey, verifiedSvp);
		queryClient.setQueriesData<{ servicesProviders: ServicesProvider[]; failedIds: string[] }>(
			{ queryKey: servicesProviders.batch._def },
			(old) => {
				if (!old) return old;
				return {
					...old,
					servicesProviders: old.servicesProviders.map((sp) =>
						sp.id === servicesProviderId ? verifiedSvp! : sp,
					),
				};
			},
		);

		navigate(paths.settingsSection.getHref(servicesProviderId, SettingsSection.ProductsAiBdc), {
			replace: true,
		});
	}, [servicesProviderId, navigate, queryClient]);

	// ── Stable refs for callbacks used inside effects ──
	// Effects must NOT depend on callback identities — useMutation / useCallback return
	// new references on re-renders, which would trigger effect cleanup and re-entry.
	const refetchRecordRef = useRef(refetchRecord);
	refetchRecordRef.current = refetchRecord;

	const provisionAndRedirectRef = useRef(provisionAndRedirect);
	provisionAndRedirectRef.current = provisionAndRedirect;

	const completeExistingAndRedirectRef = useRef(completeExistingAndRedirect);
	completeExistingAndRedirectRef.current = completeExistingAndRedirect;

	// Path 2 — Existing dealer: complete-onboarding sets supported=true itself, so there's
	// no need to wait for the Stripe webhook. Proceed immediately on mount.
	useEffect(() => {
		if (!isExistingDealer) return;
		if (hasStartedPolling.current) return;
		hasStartedPolling.current = true;

		void completeExistingAndRedirectRef.current();

		return () => { hasStartedPolling.current = false; };
	}, [isExistingDealer]);

	// Path 1 — New dealer: poll OnboardingRecord until SUBSCRIBED, then provision.
	useEffect(() => {
		if (isExistingDealer || !recordId) return;
		if (hasStartedPolling.current) return;

		hasStartedPolling.current = true;
		let attempts = 0;
		const maxAttempts = 15;


		const interval = setInterval(async () => {
			attempts++;

			const { data: freshRecord } = await refetchRecordRef.current();
			if (freshRecord?.status === OnboardingStatus.Subscribed) {
				clearInterval(interval);
				await provisionAndRedirectRef.current();
				return;
			}

			if (attempts >= maxAttempts) {
				clearInterval(interval);
				setPollingTimedOut(true);
			}
		}, 2000);

		return () => {
			clearInterval(interval);
			hasStartedPolling.current = false;
		};
	}, [isExistingDealer, recordId]);

	// Path 1 only — timeout fallback for new dealer provisioning
	const handleTimeoutContinue = useCallback(() => {
		provisionAndRedirect();
	}, [provisionAndRedirect]);

	if (!recordId && !isExistingDealer) {
		return null;
	}

	return (
		<div className='onboarding-step'>
			<div className='onboarding-step__header'>
				<h2>Processing your payment</h2>
				<p>Please wait while we confirm your payment and set up your account.</p>
			</div>

			{!pollingTimedOut && !error && (
				<div className='onboarding-step__status'>
					<Spinner size='medium' />
					<p>
						{isProvisioning
							? 'Setting up your account...'
							: 'Payment confirmed — activating your subscription...'}
					</p>
				</div>
			)}

			{pollingTimedOut && !error && (
				<div className='onboarding-step__status'>
					<p>Your payment was received. Activation may take a moment.</p>
					<Button
						variant='filled'
						isDisabled={isProvisioning}
						onPress={handleTimeoutContinue}>
						{isProvisioning ? 'Setting up...' : 'Continue'}
					</Button>
				</div>
			)}

			{error && (
				<div className='onboarding-step__payment-error'>
					{error}
				</div>
			)}
		</div>
	);
}
